import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { AreaChart, type AreaChartData } from "@/shared/ui/AreaChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import {
  BanknoteIcon,
  DownloadIcon,
  GraduationCapIcon,
  HomeIcon,
  PackageIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { useRevenueSummaryQuery, useRevenueTrendQuery } from "../api/queries";
import {
  RevenueSalesReportFilter,
  type RevenueSalesReportSearch,
} from "../ui/RevenueSalesReportFilter";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
} from "../ui/ReportSummaryStatCard";
import { RevenueTrendTable } from "../ui/RevenueTrendTable";

function useReportQuery(search: RevenueSalesReportSearch) {
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const dateFrom = search.dateFrom ?? defaultFrom;
  const dateTo = search.dateTo ?? defaultTo;
  const groupBy = search.groupBy ?? "day";

  const summaryQuery = useMemo(
    () => ({
      dateFrom,
      dateTo,
      issuedById: search.issuedById,
    }),
    [dateFrom, dateTo, search.issuedById],
  );

  const trendQuery = useMemo(
    () => ({
      dateFrom,
      dateTo,
      groupBy,
      issuedById: search.issuedById,
    }),
    [dateFrom, dateTo, groupBy, search.issuedById],
  );

  return { dateFrom, dateTo, summaryQuery, trendQuery };
}

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function RevenueSalesReportPage() {
  const search = useSearch({
    from: "/app/reports/revenue",
  }) as RevenueSalesReportSearch;

  const { dateFrom, dateTo, summaryQuery, trendQuery } = useReportQuery(search);

  const { data: summary, isLoading: summaryLoading } =
    useRevenueSummaryQuery(summaryQuery);
  const { data: trend = [], isLoading: trendLoading } =
    useRevenueTrendQuery(trendQuery);

  const exportCsv = useCallback(() => {
    const header = "ວັນທີ,ລາຍຮັບທັງໝົດ,ລາຍຮັບແພັກເກດ,ລາຍຮັບຮຽນສ່ວນຕົວ,ລາຍຮັບຄ່າເຊົ່າຫ້ອງ";
    const rows = trend.map(
      (t) =>
        `${escapeCsvCell(formatDate(t.period))},${escapeCsvCell(t.totalRevenue)},${escapeCsvCell(t.packageRevenue)},${escapeCsvCell(t.privateLessonRevenue)},${escapeCsvCell(t.roomBookingRevenue)}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [trend, dateFrom, dateTo]);

  const areaChartData: AreaChartData[] = useMemo(
    () =>
      trend.map((t) => ({
        period: formatDate(t.period),
        totalRevenue: t.totalRevenue,
        packageRevenue: t.packageRevenue,
        privateLessonRevenue: t.privateLessonRevenue,
        roomBookingRevenue: t.roomBookingRevenue,
      })),
    [trend],
  );

  const areaChartConfig: ChartConfig = {
    totalRevenue: {
      label: "ລາຍຮັບທັງໝົດ",
      color: "var(--chart-1)",
    },
    packageRevenue: {
      label: "ລາຍຮັບແພັກເກດ",
      color: "var(--chart-2)",
    },
    privateLessonRevenue: {
      label: "ລາຍຮັບຮຽນສ່ວນຕົວ",
      color: "var(--chart-3)",
    },
    roomBookingRevenue: {
      label: "ລາຍຮັບຄ່າເຊົ່າຫ້ອງ",
      color: "var(--chart-4)",
    },
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານລາຍຮັບ / ຍອດຂາຍ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານລາຍຮັບ / ລາຍງານຍອດຂາຍ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ລາຍຮັບຈາກໃບເກັບເງິນ ແລະການຮັບເງິນຕາມໃບເກັບເງິນ (ຊຳລະບາງສ່ວນ) ຈັດແຍກຕາມແພັກເກດ,
              ຮຽນສ່ວນຕົວ ແລະຄ່າເຊົ່າຫ້ອງ · 「ລາຍຮັບແພັກເກດ」ຕົງກັບລາຍງານຍອດຂາຍແພັກເກດ (ແພັກເກດຄລາດສ໌)
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={trend.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <RevenueSalesReportFilter />
        </div>

        <ReportSummaryStatGrid columns={4}>
          <ReportSummaryStatCard
            title="ລາຍຮັບທັງໝົດ"
            icon={BanknoteIcon}
            tone="revenue"
            isLoading={summaryLoading}
            amountKip={summary?.totalRevenue ?? 0}
          />
          <ReportSummaryStatCard
            title="ລາຍຮັບແພັກເກດ"
            icon={PackageIcon}
            tone="packages"
            isLoading={summaryLoading}
            amountKip={summary?.packageRevenue ?? 0}
          />
          <ReportSummaryStatCard
            title="ລາຍຮັບຮຽນສ່ວນຕົວ"
            icon={GraduationCapIcon}
            tone="students"
            isLoading={summaryLoading}
            amountKip={summary?.privateLessonRevenue ?? 0}
          />
          <ReportSummaryStatCard
            title="ລາຍຮັບຄ່າເຊົ່າຫ້ອງ"
            icon={HomeIcon}
            tone="attendance"
            isLoading={summaryLoading}
            amountKip={summary?.roomBookingRevenue ?? 0}
          />
        </ReportSummaryStatGrid>

        {areaChartData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>ແນວໂນ້ມລາຍຮັບ</CardTitle>
              <p className="text-muted-foreground text-sm">
                ລາຍຮັບຕາມວັນທີ ຫຼື ເດືອນ
              </p>
            </CardHeader>
            <CardContent>
              <AreaChart
                data={areaChartData}
                config={areaChartConfig}
                xDataKey="period"
                valueDataKey={[
                  "totalRevenue",
                  "packageRevenue",
                  "privateLessonRevenue",
                  "roomBookingRevenue",
                ]}
                stacked
                xAxisTickFormatter={(v) =>
                  typeof v === "string" ? v.slice(0, 10) : String(v)
                }
                className="mx-auto aspect-video max-h-[350px]"
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>ຕາຕະລາງລາຍວັນລາຍຮັບ</CardTitle>
            <p className="text-muted-foreground text-sm">
              ລາຍຮັບຕາມວັນທີ ຫຼື ເດືອນໃນຊ່ວງທີ່ເລືອກ
            </p>
          </CardHeader>
          <CardContent>
            <RevenueTrendTable isLoading={trendLoading} data={trend} />
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
